import { useState } from 'react';
import { FileText, Plus, Undo2 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreateSupplierInvoiceMutation, useCreateSupplierReturnMutation, usePaymentMethodsQuery,
  usePaySupplierMutation, useProductsQuery, useSupplierInvoicesQuery, useSupplierReturnActionMutation,
  useSupplierReturnsQuery, useSuppliersQuery, useVoidSupplierInvoiceMutation,
  type SupplierInvoice, type SupplierReturn,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';

type Tab = 'invoices' | 'returns';

/**
 * What we owe suppliers and goods going back to them.
 *
 * Receiving goods and owing money for them are separate on purpose: a delivery
 * moves stock, the supplier's invoice creates the debt. This screen is the
 * second half, which is why it lives apart from Goods Received.
 */
export function PayablesPage() {
  const [tab, setTab] = useState<Tab>('invoices');

  return (
    <>
      <PageHeader
        title="Payables"
        description="Supplier bills, what you have paid, and goods sent back."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={tab === 'invoices' ? 'primary' : 'outline'}
          onClick={() => setTab('invoices')}
        >
          Supplier invoices
        </Button>
        <Button
          size="sm"
          variant={tab === 'returns' ? 'primary' : 'outline'}
          onClick={() => setTab('returns')}
        >
          Returns to suppliers
        </Button>
      </div>

      {tab === 'invoices' ? <InvoicesPanel /> : <ReturnsPanel />}
    </>
  );
}

// -------------------------------------------------------------------- invoices

function InvoicesPanel() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [filter, setFilter] = useState<'all' | 'unpaid' | 'overdue'>('all');
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useSupplierInvoicesQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    status: filter === 'unpaid' ? 'POSTED' : undefined,
    overdueOnly: filter === 'overdue' || undefined,
  });
  const { data: suppliers } = useSuppliersQuery({ limit: 200, isActive: true });
  const { data: methods } = usePaymentMethodsQuery();

  const [createInvoice, createState] = useCreateSupplierInvoiceMutation();
  const [voidInvoice, voidState] = useVoidSupplierInvoiceMutation();
  const [paySupplier, payState] = usePaySupplierMutation();

  const [recording, setRecording] = useState(false);
  const [form, setForm] = useState({ supplierId: '', supplierRef: '', subtotal: '', taxAmount: '' });
  const [paying, setPaying] = useState<SupplierInvoice | null>(null);
  const [payment, setPayment] = useState({ methodId: '', amount: '' });
  const [voiding, setVoiding] = useState<SupplierInvoice | null>(null);
  const [reason, setReason] = useState('');

  const record = async (): Promise<void> => {
    setFailure(null);
    try {
      await createInvoice({
        supplierId: form.supplierId,
        supplierRef: form.supplierRef || undefined,
        subtotal: Number(form.subtotal),
        taxAmount: form.taxAmount ? Number(form.taxAmount) : undefined,
      }).unwrap();
      setRecording(false);
      setForm({ supplierId: '', supplierRef: '', subtotal: '', taxAmount: '' });
    } catch (recordError) {
      setFailure(apiErrorMessage(recordError, 'That invoice could not be recorded'));
    }
  };

  const settle = async (): Promise<void> => {
    if (!paying) return;
    setFailure(null);
    try {
      await paySupplier({
        supplierId: paying.supplier.id,
        methodId: payment.methodId,
        amount: Number(payment.amount),
        allocations: [{ invoiceId: paying.id, amount: Number(payment.amount) }],
      }).unwrap();
      setPaying(null);
      setPayment({ methodId: '', amount: '' });
    } catch (payError) {
      // Overpaying is refused with the amount still due.
      setFailure(apiErrorMessage(payError, 'That payment could not be recorded'));
    }
  };

  const confirmVoid = async (): Promise<void> => {
    if (!voiding) return;
    setFailure(null);
    try {
      await voidInvoice({ id: voiding.id, reason }).unwrap();
      setVoiding(null);
      setReason('');
    } catch (voidError) {
      setFailure(apiErrorMessage(voidError, 'That invoice could not be voided'));
    }
  };

  return (
    <>
      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Bills', value: String(data.summary.count), hint: 'in this period' },
            { label: 'Billed', value: formatMoney(String(data.summary.billed), format) },
            { label: 'Paid', value: formatMoney(String(data.summary.paid), format) },
            {
              label: 'Owed to suppliers',
              value: formatMoney(String(data.summary.outstanding), format),
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card>
      <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
        <DateRangeFilter
          value={range}
          onChange={(next) => {
            setRange(next);
            setPage(1);
          }}
        />
        <div className="w-full sm:w-52">
          <Select
            label="Show"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as typeof filter);
              setPage(1);
            }}
          >
            <option value="all">All invoices</option>
            <option value="unpaid">Still owing</option>
            <option value="overdue">Overdue</option>
          </Select>
        </div>
        <div className="ml-auto">
          <Can permission={P.PURCHASE_CREATE}>
            <Button size="sm" icon={Plus} onClick={() => setRecording(true)}>
              Record a bill
            </Button>
          </Can>
        </div>
      </div>

      {failure && (
        <div className="p-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={filter === 'all' ? 'No supplier bills yet' : 'Nothing matches'}
          description="Receiving goods moves stock; a bill is what creates the debt."
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Number</Th>
                <Th>Supplier</Th>
                <Th>Their ref</Th>
                <Th>Due</Th>
                <Th numeric>Total</Th>
                <Th numeric>Outstanding</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className={isFetching ? 'opacity-60' : undefined}>
              {data?.items.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-sunken">
                  <Td className="font-medium">{invoice.number}</Td>
                  <Td className="text-secondary">{invoice.supplier.name}</Td>
                  <Td className="text-secondary">{invoice.supplierRef ?? '—'}</Td>
                  <Td className="text-secondary">
                    {invoice.dueDate ? formatDate(invoice.dueDate, format) : '—'}
                  </Td>
                  <Td numeric>{formatMoney(invoice.total, format)}</Td>
                  <Td
                    numeric
                    className={Number(invoice.dueAmount) > 0 ? 'text-amber-600' : 'text-secondary'}
                  >
                    {formatMoney(invoice.dueAmount, format)}
                  </Td>
                  <Td>
                    <Badge
                      tone={
                        invoice.status === 'VOID'
                          ? 'neutral'
                          : invoice.status === 'PAID'
                            ? 'success'
                            : 'warning'
                      }
                    >
                      {invoice.status.toLowerCase()}
                    </Badge>
                  </Td>
                  <Td className="text-right">
                    {invoice.status !== 'VOID' && Number(invoice.dueAmount) > 0 && (
                      <>
                        <Can permission={P.PAYMENT_CREATE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setFailure(null);
                              setPaying(invoice);
                              setPayment({
                                methodId: methods?.find((m) => m.isDefault)?.id ?? methods?.[0]?.id ?? '',
                                amount: invoice.dueAmount,
                              });
                            }}
                          >
                            Pay
                          </Button>
                        </Can>
                        <Can permission={P.PURCHASE_UPDATE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setFailure(null);
                              setVoiding(invoice);
                            }}
                          >
                            Void
                          </Button>
                        </Can>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data && (
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <Modal
        open={recording}
        onClose={() => setRecording(false)}
        title="Record a supplier bill"
        description="This is what creates the payable. Receiving the goods only moved stock."
        footer={
          <>
            <Button variant="outline" onClick={() => setRecording(false)}>Cancel</Button>
            <Button
              loading={createState.isLoading}
              disabled={!form.supplierId || !form.subtotal}
              onClick={() => void record()}
            >
              Record bill
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Supplier"
            required
            value={form.supplierId}
            onChange={(event) => setForm({ ...form, supplierId: event.target.value })}
          >
            <option value="">Select a supplier</option>
            {suppliers?.items.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </Select>

          <Input
            label="Their invoice number"
            value={form.supplierRef}
            onChange={(event) => setForm({ ...form, supplierRef: event.target.value })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Subtotal"
              required
              type="number"
              min="0"
              step="0.01"
              value={form.subtotal}
              onChange={(event) => setForm({ ...form, subtotal: event.target.value })}
              hint="Net of tax."
            />
            <Input
              label="Tax"
              type="number"
              min="0"
              step="0.01"
              value={form.taxAmount}
              onChange={(event) => setForm({ ...form, taxAmount: event.target.value })}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={paying !== null}
        onClose={() => setPaying(null)}
        title="Pay this bill"
        footer={
          <>
            <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
            <Button
              loading={payState.isLoading}
              disabled={!payment.methodId || !payment.amount || Number(payment.amount) <= 0}
              onClick={() => void settle()}
            >
              Record payment
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {paying && (
            <p className="text-sm text-secondary">
              {paying.number} · {paying.supplier.name} ·{' '}
              {formatMoney(paying.dueAmount, format)} outstanding
            </p>
          )}
          <Select
            label="Method"
            value={payment.methodId}
            onChange={(event) => setPayment({ ...payment, methodId: event.target.value })}
          >
            {methods?.map((method) => (
              <option key={method.id} value={method.id}>{method.name}</option>
            ))}
          </Select>
          <Input
            label="Amount"
            type="number"
            min="0"
            step="0.01"
            value={payment.amount}
            onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
          />
        </div>
      </Modal>

      <Modal
        open={voiding !== null}
        onClose={() => setVoiding(null)}
        title="Void this bill?"
        description="The payable is reversed. A bill that has been paid against cannot be voided."
        footer={
          <>
            <Button variant="outline" onClick={() => setVoiding(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={voidState.isLoading}
              disabled={!reason}
              onClick={() => void confirmVoid()}
            >
              Void bill
            </Button>
          </>
        }
      >
        <Input label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      </Modal>
      </Card>
    </>
  );
}

// --------------------------------------------------------------------- returns

function ReturnsPanel() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [failure, setFailure] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useSupplierReturnsQuery({
    page,
    limit: 25,
  });
  const { data: suppliers } = useSuppliersQuery({ limit: 200, isActive: true });
  const { data: products } = useProductsQuery({ limit: 200, status: 'ACTIVE' });

  const [createReturn, createState] = useCreateSupplierReturnMutation();
  const [runAction, actionState] = useSupplierReturnActionMutation();

  const [supplierId, setSupplierId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name} — ${variant.sku}`,
    })),
  );

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await createReturn({
        supplierId,
        reason: reason || undefined,
        lines: [{ variantId, quantity: Number(quantity) }],
      }).unwrap();
      setCreating(false);
      setSupplierId('');
      setVariantId('');
      setQuantity('');
      setReason('');
    } catch (createError) {
      setFailure(apiErrorMessage(createError, 'That return could not be raised'));
    }
  };

  const act = async (id: string, action: string): Promise<void> => {
    setFailure(null);
    try {
      await runAction({ id, action }).unwrap();
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `That return could not be ${action}ed`));
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-end border-b border-subtle p-4">
        <Can permission={P.PURCHASE_RETURN}>
          <Button size="sm" icon={Plus} onClick={() => setCreating(true)}>
            Return goods
          </Button>
        </Can>
      </div>

      {failure && (
        <div className="p-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyState
          icon={Undo2}
          title="No returns to suppliers yet"
          description="Approving a return sends the goods back and credits what you owe."
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Number</Th>
                <Th>Supplier</Th>
                <Th>Raised</Th>
                <Th>Reason</Th>
                <Th numeric>Credit</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className={isFetching ? 'opacity-60' : undefined}>
              {data?.items.map((entry: SupplierReturn) => (
                <tr key={entry.id} className="hover:bg-sunken">
                  <Td className="font-medium">{entry.number}</Td>
                  <Td className="text-secondary">{entry.supplier.name}</Td>
                  <Td className="text-secondary">{formatDate(entry.returnedAt, format)}</Td>
                  <Td className="text-secondary">{entry.reason ?? '—'}</Td>
                  <Td numeric>{formatMoney(entry.total, format)}</Td>
                  <Td>
                    <Badge tone={entry.status === 'COMPLETED' ? 'success' : 'neutral'}>
                      {entry.status.toLowerCase().replace(/_/g, ' ')}
                    </Badge>
                  </Td>
                  <Td className="text-right">
                    {(entry.status === 'DRAFT' || entry.status === 'PENDING_INSPECTION') && (
                      <Can permission={P.PURCHASE_RETURN}>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={actionState.isLoading}
                          onClick={() => void act(entry.id, 'cancel')}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={actionState.isLoading}
                          onClick={() => void act(entry.id, 'approve')}
                        >
                          Approve
                        </Button>
                      </Can>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data && (
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Return goods to a supplier"
        description="Approving it takes the stock out and reduces what you owe them."
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              loading={createState.isLoading}
              disabled={!supplierId || !variantId || !quantity}
              onClick={() => void submit()}
            >
              Raise return
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Supplier"
            required
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
          >
            <option value="">Select a supplier</option>
            {suppliers?.items.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </Select>

          <Select
            label="Product"
            required
            value={variantId}
            onChange={(event) => setVariantId(event.target.value)}
          >
            <option value="">Select a product</option>
            {variantOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </Select>

          <Input
            label="Quantity"
            required
            type="number"
            min="0"
            step="0.0001"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            hint="Valued at what the goods are carried at, so the credit matches the stock leaving."
          />

          <Input
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </Card>
  );
}
